import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import Div100vh from 'react-div-100vh';
import { RootState, useAppDispatch } from 'redux/store';

import themes from 'styles/themes';
import ChatSidebarList from './ChatSidebarList';
import ChatSidebarFooter from './ChatSidebarFooter';
import ChatSidebarHeader from './ChatSidebarHeader';
import './styles.scss';
import { useLazyQuery } from '@apollo/client';
import { getConversation, getConversationVariables } from '__generated__/getConversation';
import { GET_CONVERSATION, GET_CONVERSATION_LIST } from 'graph_ql/chat/queries.gql';
import { useEffect, useState } from 'react';
import constant from 'utils/constant';
import UserType from 'constants/user';
import { useChatContext, ARCHIVED_THREAD } from 'containers/chat/hooks/ChatContext';
import {
    updateConversationList,
    updateIsArchivedThread,
    updateIsDisplayArchivedThread,
    updateArchivedThreadDate,
    removeConversationState,
    removeReduxConversation,
    removeReduxMessage,
} from '../../redux/chat/chat.slice';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
    list: {
        width: 300,
        background: themes.palette.primary.light,
        display: 'flex',
        justifyContent: 'space-between',
        flexDirection: 'column',
        padding: '0 24px',
    },
    logo: {
        width: '69px',
        margin: '40px auto',
    },
    navLink: {
        textDecoration: 'none',
        color: themes.palette.primary.main,
        fontWeight: 600,
        fontSize: 14,
        display: 'block',
        margin: '24px 0',
        padding: '0 0 10px 0',
        fontFamily: 'PoppinsRegular',
        cursor: 'pointer',
    },
}));
const DEFAULT_CONVERSATION_TITLE_SIZE = 10;
const DEFAULT_CONVERSATION_SIZE = 1;
const DEFAULT_PAGE_NUMBER = 0;
function ChatSidebar({
    isOpen,
    setIsOpen,
    handleListOptionClick,
    handleRedirectToStyleQuiz,
    handleNewConversation,
    clearChatWindow,
    handleFooterClick,
}) {
    const classes = useStyles();
    const { getConversationFn, canFetchConversationList, user } = useChatContext();
    const [isApiInvoked, setIsApiInvoked] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const isDisplayArchivedThread = useSelector((state: RootState) => state.chat.isDisplayArchivedThread);
    const archivedThreadDate = useSelector((state: RootState) => state.chat.archivedThreadDate);

    const [getConversationList, { loading: conversationListLoading }] = useLazyQuery(GET_CONVERSATION_LIST, {
        onCompleted(response) {
            let tempHits = response?.algoliaGetConversation ? [...response?.algoliaGetConversation?.hits] : [];
            const updatedConversations = [...tempHits];
            dispatch(
                updateConversationList({
                    value: updatedConversations,
                }),
            );
            if (updatedConversations?.length) {
                const updatedConversationsLength = updatedConversations?.length;
                const lastConversationDate = updatedConversationsLength
                    ? updatedConversations[updatedConversationsLength - 1]?.updated
                    : '';
                if (lastConversationDate) {
                    dispatch(
                        updateArchivedThreadDate({
                            value: lastConversationDate,
                        }),
                    );
                }
                checkArchievedChats(lastConversationDate ? lastConversationDate : undefined);
            } else {
                // to check if there are any old conversations for this user at the moment the ticket released.
                checkArchievedChats(undefined);
            }
        },
        fetchPolicy: 'network-only',
        onError(error) {
            console.error('conversationlist Failed: ', error);
        },
    });

    const [getPreviousConversation, { loading: isArchivedThreadLoading }] = useLazyQuery<
        getConversation,
        getConversationVariables
    >(GET_CONVERSATION, {
        context: {
            allowAnonymousAuth: true,
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'standby',
        onCompleted(response: any) {
            if (response?.conversations?.data?.length) {
                const isOpeningConversation =
                    response?.conversations?.data[0]?.id === `_opening_conversation_${user?.id}` &&
                    response?.conversations?.data[0]?.messages?.length === 1;
                if (!isOpeningConversation) {
                    dispatch(
                        updateIsArchivedThread({
                            value: true,
                        }),
                    );
                }
            }
        },
    });

    const checkArchievedChats = (lastUpdatedDate: string = archivedThreadDate) => {
        getPreviousConversation({
            variables: {
                state: new Date(),
                input: {
                    paging: {
                        orderBy: constant.ORDER_BY.UPDATED_DESC,
                        page: 0,
                        size: 1,
                    },
                    lastUpdated: lastUpdatedDate,
                },
            },
        });
    };

    const clearDisplayArchivedThread = () => {
        dispatch(
            removeConversationState({
                key: ARCHIVED_THREAD,
            }),
        );
        dispatch(
            removeReduxConversation({
                key: ARCHIVED_THREAD,
            }),
        );
        dispatch(
            removeReduxMessage({
                key: ARCHIVED_THREAD,
            }),
        );
    };

    const handleArchivedThread = () => {
        if (isDisplayArchivedThread) {
            return;
        }
        clearDisplayArchivedThread();
        clearChatWindow();
        dispatch(
            updateIsDisplayArchivedThread({
                value: true,
            }),
        );
        getConversationFn({
            variables: {
                input: {
                    paging: {
                        orderBy: constant.ORDER_BY.UPDATED_DESC,
                        page: DEFAULT_PAGE_NUMBER,
                        size: DEFAULT_CONVERSATION_SIZE,
                    },
                    lastUpdated: archivedThreadDate,
                },
            },
        });
    };

    useEffect(() => {
        if (isOpen && canFetchConversationList()) {
            getConversationList({
                variables: {
                    query: ``,
                    filters: `userId: ${user?.id}`,
                    page: DEFAULT_PAGE_NUMBER,
                    hit: DEFAULT_CONVERSATION_TITLE_SIZE,
                },
            });
        }
    }, [isOpen, getConversationList, user?.id, canFetchConversationList]);

    useEffect(() => {
        if (!isApiInvoked && canFetchConversationList()) {
            getConversationList({
                variables: {
                    query: ``,
                    filters: `userId: ${user?.id}`,
                    page: DEFAULT_PAGE_NUMBER,
                    hit: DEFAULT_CONVERSATION_TITLE_SIZE,
                },
            });
            setIsApiInvoked(true);
        }
    }, [isApiInvoked, getConversationList, user?.id, canFetchConversationList]);

    const handleCloseSideBar = () => {
        setIsOpen(false);
        setIsApiInvoked(false);
    };

    return (
        <Div100vh className={clsx(classes.list)} role="presentation" onClick={() => handleCloseSideBar()}>
            <div className="chat-sidebar-container">
                <ChatSidebarHeader handleNewConversation={handleNewConversation} />
                <div className="chat-list-container">
                    <ChatSidebarList
                        handleListOptionClick={handleListOptionClick}
                        handleNewConversation={handleNewConversation}
                        conversationListLoading={conversationListLoading}
                        isArchivedThreadLoading={isArchivedThreadLoading}
                        handleArchivedThread={handleArchivedThread}
                    />
                </div>
                {user?.userType !== UserType.Stylist && (
                    <ChatSidebarFooter
                        handleRedirectToStyleQuiz={handleRedirectToStyleQuiz}
                        handleFooterClick={handleFooterClick}
                    />
                )}
            </div>
        </Div100vh>
    );
}
export default ChatSidebar;
